import type { TFunction } from "i18next";
import type {
  ChatChannel,
  IChatNoticeDay,
  IChatNoticeMetadata,
} from "@/types/(my-space)/chat.interface";
import { MS_PAYROLL_PATHS } from "@/routes/my-space/routes.payroll";
import { MS_TIME_ATTENDANCE_PATHS } from "@/routes/my-space/routes.time-attendance";

/**
 * App channels (Time & Attendance): read-only chats the server posts notices
 * to. The server stores a kind + data; everything a person reads is written
 * here, in their language — like system messages.
 */

/** Which app channel a conversation is (undefined: not a channel, or an old row). */
export const channelOf = (
  c: { type?: string; metadata?: unknown } | null | undefined,
): ChatChannel | undefined => {
  if (c?.type !== "channel" || !c.metadata || typeof c.metadata !== "object") return undefined;
  return (c.metadata as { channel?: ChatChannel }).channel;
};

/** The official MaiHRMS account: verified badge, logo, product updates. */
export const isOfficialChannel = (channel: ChatChannel | string | undefined) =>
  channel === "product_updates";

/** The line under a channel's name. */
export const channelSubtitle = (t: TFunction, channel: ChatChannel | string | undefined) =>
  t(`chat:channel.subtitles.${channel ?? "time_attendance"}`, {
    defaultValue: t("chat:channel.subtitle"),
  });

/** Where the composer would be: who may write here. */
export const channelReadOnly = (t: TFunction, channel: ChatChannel | string | undefined) =>
  t(`chat:channel.read_only_by.${channel ?? "time_attendance"}`, {
    defaultValue: t("chat:channel.read_only"),
  });

/** A feature_update notice's data, defensively read. */
export const featureUpdateOf = (notice: IChatNoticeMetadata) => {
  const d = notice.data ?? {};
  const mediaUrl = str(d.mediaUrl);
  return {
    title: str(d.title) ?? "",
    body: typeof d.body === "string" ? d.body : "",
    mediaUrl,
    mediaKind: mediaUrl ? (d.mediaKind === "video" ? "video" : "image") : undefined,
  } as const;
};

/** A channel's name in the reader's language (the stored one is English). */
export const channelName = (t: TFunction, channel: ChatChannel | string | undefined) =>
  t(`chat:channel.names.${channel ?? "time_attendance"}`, {
    defaultValue: t("chat:channel.names.time_attendance"),
  });

/** "25:29" — hours:minutes, as on a timesheet. */
export const formatHoursMinutes = (minutes: number) => {
  const total = Math.max(0, Math.round(minutes));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/** "25.48" — decimal hours (the report's regular/overtime line). */
export const formatDecimalHours = (minutes: number) =>
  (Math.max(0, minutes) / 60).toFixed(2);

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);
const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined);

export interface WeeklyHours {
  weekStart: string;
  weekEnd: string;
  days: IChatNoticeDay[];
  totalMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;
}

/** A weekly_hours notice's data, defensively read (older/partial rows). */
export const weeklyHoursOf = (notice: IChatNoticeMetadata): WeeklyHours => {
  const d = notice.data ?? {};
  const days = Array.isArray(d.days)
    ? (d.days as Partial<IChatNoticeDay>[]).map((x) => ({
        date: String(x.date ?? ""),
        workedMinutes: num(x.workedMinutes),
        overtimeMinutes: num(x.overtimeMinutes),
      }))
    : [];
  return {
    weekStart: String(d.weekStart ?? ""),
    weekEnd: String(d.weekEnd ?? ""),
    days,
    totalMinutes: num(d.totalMinutes),
    regularMinutes: num(d.regularMinutes),
    overtimeMinutes: num(d.overtimeMinutes),
  };
};

/** The card's headline. */
export const noticeTitle = (t: TFunction, notice: IChatNoticeMetadata) =>
  // A product update is written by MaiHRMS, already worded.
  (notice.kind === "feature_update" ? str(notice.data?.title) : undefined) ??
  t(`chat:notice.${notice.kind}.title`, { defaultValue: t("chat:notice.fallback") });

/**
 * The card's sentence (empty for the weekly report — its chart says it).
 * [formatPeriod]: not needed — the period comes from payroll already worded.
 */
export const noticeBody = (t: TFunction, notice: IChatNoticeMetadata): string => {
  const d = notice.data ?? {};
  const minutes = num(d.minutes);
  switch (notice.kind) {
    case "weekly_hours":
    case "feature_update":
      return "";
    case "checkin_reminder":
    case "checkout_reminder":
      return minutes > 0
        ? t(`chat:notice.${notice.kind}.body_in`, { duration: formatDuration(t, minutes) })
        : t(`chat:notice.${notice.kind}.body`);
    case "late_checkin_reminder":
    case "late_checkout_reminder":
    case "auto_clock_out":
      return t(`chat:notice.${notice.kind}.body`);
    case "admin_clocked_in":
    case "admin_clocked_out": {
      const name = str(d.adminName);
      return name
        ? t(`chat:notice.${notice.kind}.body_by`, { name })
        : t(`chat:notice.${notice.kind}.body`);
    }
    case "payslip_ready":
    case "salary_paid": {
      const period = str(d.period);
      return period
        ? t(`chat:notice.${notice.kind}.body_for`, { period })
        : t(`chat:notice.${notice.kind}.body`);
    }
    default:
      return "";
  }
};

/** "15 min" / "1 h 30 min" for a reminder's offset. */
export const formatDuration = (t: TFunction, minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (!h) return t("chat:notice.duration_m", { m });
  if (!m) return t("chat:notice.duration_h", { h });
  return t("chat:notice.duration_hm", { h, m });
};

/**
 * One line for lists and popups: the headline, plus the total for a
 * weekly report.
 */
export const noticePreview = (t: TFunction, notice: IChatNoticeMetadata | undefined) => {
  if (!notice) return t("chat:notice.fallback");
  if (notice.kind === "weekly_hours") {
    return t("chat:notice.weekly_hours.preview", {
      total: formatHoursMinutes(weeklyHoursOf(notice).totalMinutes),
    });
  }
  const body = noticeBody(t, notice);
  return body || noticeTitle(t, notice);
};

/** The card's button: where it goes (a function: route files import pages). */
export const noticeAction = (
  t: TFunction,
  notice: IChatNoticeMetadata,
): { label: string; path: string } | null => {
  switch (notice.kind) {
    case "weekly_hours":
    case "admin_clocked_in":
    case "admin_clocked_out":
      return {
        label: t("chat:notice.actions.view_attendance"),
        path: MS_TIME_ATTENDANCE_PATHS.ATTENDANCE_SUMMARY,
      };
    case "checkin_reminder":
    case "late_checkin_reminder":
    case "checkout_reminder":
    case "late_checkout_reminder":
      return { label: t("chat:notice.actions.open_attendance"), path: MS_TIME_ATTENDANCE_PATHS.ROOT };
    case "auto_clock_out":
      return {
        label: t("chat:notice.actions.request_correction"),
        path: MS_TIME_ATTENDANCE_PATHS.ADD_REGULARIZATION,
      };
    case "payslip_ready":
    case "salary_paid":
      return { label: t("chat:notice.actions.view_payslips"), path: MS_PAYROLL_PATHS.ROOT };
    default:
      return null;
  }
};
